import type { LoggedInUserProps } from '@interfaces/auth';
import { normalizeUserRole, USER_ROLE } from '@utils/auth/roles';
import { links, v2Links } from '@utils/navigation/links';
import { BarChart10, LineChartUp02, LineChartUp04 } from '@v2/ui/Icon/Charts';
import { BookOpen01 } from '@v2/ui/Icon/education';
import { CoinsHand, CoinsStacked01, CreditCard02 } from '@v2/ui/Icon/Finance';
import { Building05 } from '@v2/ui/Icon/General';
import { Star01 } from '@v2/ui/Icon/Shapes';
import type { SVGComponentProps } from '@v2/ui/Icon/Template';
import { User01, UserCheck01, Users01 } from '@v2/ui/Icon/Users';
import { useMemo, type FC } from 'react';

interface DashboardSection {
  title: string | null;
  mobileTitle: string;
  items: DashboardRoute[];
}

export interface DashboardRoute {
  title: string;
  path: string;
  roles: USER_ROLE[];
  childs?: string[];
  icon: FC<SVGComponentProps>;
}

export const V2_DASHBOARD_LINKS: DashboardSection[] = [
  {
    title: 'MY ACCOUNT',
    mobileTitle: 'My Account',
    items: [
      {
        icon: Star01,
        path: v2Links.favorites,
        title: 'Favorites',
        roles: [
          USER_ROLE.user,
          USER_ROLE.admin,
          USER_ROLE.superAdmin,
          USER_ROLE.affiliate,
        ],
      },
      {
        icon: CreditCard02,
        path: v2Links.orders,
        title: 'Orders',
        roles: [
          USER_ROLE.user,
          USER_ROLE.admin,
          USER_ROLE.superAdmin,
          USER_ROLE.affiliate,
        ],
      },
      {
        title: 'Rewards',
        icon: CoinsStacked01,
        path: v2Links.rewards,

        roles: [
          USER_ROLE.user,
          USER_ROLE.admin,
          USER_ROLE.superAdmin,
          USER_ROLE.affiliate,
        ],
      },
      {
        icon: User01,
        title: 'Profile & Travellers',
        path: v2Links.profile,
        childs: [v2Links.travelers],

        roles: [
          USER_ROLE.user,
          USER_ROLE.admin,
          USER_ROLE.superAdmin,
          USER_ROLE.affiliate,
        ],
      },

      // {
      //   icon: Bell02,
      //   title: 'Notifications',
      //   path: v2Links.notifications,

      //   roles: [
      //     USER_ROLE.user,
      //     USER_ROLE.admin,
      //     USER_ROLE.superAdmin,
      //     USER_ROLE.affiliate,
      //   ],
      // },
    ],
  },

  {
    title: 'PARTNER',
    mobileTitle: 'Partner',
    items: [
      {
        title: 'Overview',
        path: v2Links.partnersOverview,
        roles: [USER_ROLE.agency, USER_ROLE.agencyMember],
        icon: LineChartUp04,
      },
      {
        title: 'Reports',
        path: v2Links.partnerReports,
        roles: [USER_ROLE.agency, USER_ROLE.agencyMember],
        icon: BarChart10,
      },
      {
        title: 'My team',
        path: v2Links.partnerTeams,
        roles: [USER_ROLE.agency],
        icon: Users01,
      },
      {
        title: 'Clients',
        path: v2Links.clients,
        roles: [USER_ROLE.agency, USER_ROLE.agencyMember],
        icon: UserCheck01,
        childs: ['/dashboard/clients'],
      },
      {
        title: 'Performance',
        path: v2Links.partnersPerformance,
        roles: [USER_ROLE.agency],
        icon: LineChartUp02,
      },
      {
        title: 'Sales',
        path: v2Links.partnerSales,
        roles: [USER_ROLE.agency, USER_ROLE.agencyMember],
        icon: CoinsHand,
      },
      {
        title: 'Partner rewards',
        path: v2Links.partnerPromoCodes,
        roles: [USER_ROLE.agency],
        icon: CoinsStacked01,
      },
    ],
  },
  {
    title: 'Partner',
    mobileTitle: 'Partner',
    items: [
      {
        icon: LineChartUp04,
        title: 'Performance',
        path: v2Links.affiliatePerformance,
        roles: [USER_ROLE.admin, USER_ROLE.superAdmin, USER_ROLE.affiliate],
      },
      {
        icon: CoinsHand,
        title: 'Sales',
        path: v2Links.affiliateSales,
        roles: [USER_ROLE.admin, USER_ROLE.superAdmin, USER_ROLE.affiliate],
      },
      {
        icon: CoinsStacked01,
        title: 'Rewards',
        path: v2Links.affiliatePromoCodes,
        roles: [USER_ROLE.admin, USER_ROLE.superAdmin, USER_ROLE.affiliate],
      },
    ],
  },
  {
    title: 'AGENT',
    mobileTitle: 'Agent',
    items: [
      {
        icon: UserCheck01,
        title: 'Clients',
        path: v2Links.clients,
        roles: [USER_ROLE.individualAgent],
        childs: ['/dashboard/clients'],
      },
      {
        icon: LineChartUp02,
        title: 'Performance',
        path: v2Links.affiliatePerformance,
        roles: [USER_ROLE.individualAgent],
      },
      {
        icon: CoinsHand,
        title: 'Sales',
        path: v2Links.affiliateSales,
        roles: [USER_ROLE.individualAgent],
      },
      {
        icon: CoinsStacked01,
        title: 'Agent rewards',
        path: v2Links.affiliatePromoCodes,
        roles: [USER_ROLE.individualAgent],
      },
    ],
  },
  {
    title: 'ADMIN',
    mobileTitle: 'Admin',
    items: [
      {
        icon: UserCheck01,
        title: 'Clients',
        path: v2Links.clients,
        roles: [USER_ROLE.admin, USER_ROLE.superAdmin],
        childs: ['/dashboard/clients'],
      },
      {
        icon: BarChart10,
        title: 'Reports',
        path: v2Links.adminReports,
        roles: [USER_ROLE.admin, USER_ROLE.superAdmin],
      },
      {
        icon: CreditCard02,
        title: 'Orders',
        path: v2Links.adminOrders,
        roles: [USER_ROLE.admin, USER_ROLE.superAdmin],
      },
      {
        icon: CoinsStacked01,
        title: 'Elite Cash & Rewards',
        path: v2Links.adminDiscountCodes,
        roles: [USER_ROLE.superAdmin],
        childs: [v2Links.dashboardPage('discount-code-details/:id')],
      },
      {
        icon: Users01,
        title: 'Users',
        path: v2Links.adminUsers,
        roles: [USER_ROLE.admin, USER_ROLE.superAdmin],
        childs: [v2Links.dashboardPage('user-details/:id')],
      },
      {
        icon: Building05,
        title: 'Agencies',
        path: links.adminAgencies,
        roles: [USER_ROLE.admin, USER_ROLE.superAdmin],
      },
      {
        icon: UserCheck01,
        title: 'Partners',
        path: v2Links.adminPartners,
        roles: [USER_ROLE.admin, USER_ROLE.superAdmin],
      },
      // {
      //   icon: CurrencyDollar,
      //   title: 'Remittances',
      //   path: v2Links.adminRemittances,
      //   roles: [USER_ROLE.admin, USER_ROLE.superAdmin],
      // },
      {
        icon: BookOpen01,
        title: 'Cache Logs',
        path: v2Links.adminCacheLogs,
        roles: [USER_ROLE.admin, USER_ROLE.superAdmin],
      },
      {
        icon: BookOpen01,
        title: 'Event Cache',
        path: v2Links.adminEventCache,
        roles: [USER_ROLE.admin, USER_ROLE.superAdmin],
      },
    ],
  },
];

export function getDashboardSections(role: unknown): DashboardSection[] {
  const userRole = normalizeUserRole(role);

  return V2_DASHBOARD_LINKS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.roles.includes(userRole)),
  })).filter((section) => section.items.length > 0);
}

export function useDashboardLinks(props: LoggedInUserProps | null) {
  return useMemo(() => getDashboardSections(props?.role), [props?.role]);
}
